import React, { useState, useEffect } from 'react';
import { Link  } from 'react-router-dom';
import Header from './Header';
import ProjectDropdown from './ProjectDropdown';
import { changeUserDefaultProject } from './apiUtils.js';
import EnhancedLegacyCodeAnalysisDialog from './EnhancedLegacyCodeAnalysisDialog'; //

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

// InfoIcon Component
const InfoIcon = () => (
  <svg
    className="w-4 h-4 text-gray-500 ml-2 cursor-help"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

// Updated Custom Icon Component with size prop
const CustomIcon = ({ iconPath, altText, size = "w-10 h-10" }) => (
  <img
    src={iconPath}
    alt={altText}
    className={`${size} object-contain`}
  />
);

// Tooltip Component
const Tooltip = ({ text, children }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 w-64 p-2 mt-1 text-sm text-white bg-gray-900 rounded shadow-lg -left-4 transform -translate-x-full">
          {text}
          <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
        </div>
      )}
    </div>
  );
};


// Help Dialog Component
const AdminHelpDialog = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-6xl max-h-[90vh] overflow-hidden mx-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-rose-500 to-pink-500 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Admin Functions Setup Guide</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors p-2 rounded-full hover:bg-white/20"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Introduction Text */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Project Configuration Workflow</h3>
            <p className="text-gray-600 leading-relaxed mb-4">
              The Admin Functions section provides tools to configure your project environment. The workflow diagram below
              shows the recommended sequence for setting up your project configuration. Items marked as "Mandatory" must
              be completed, while "Optional" items can be configured based on your specific project needs.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Tip:</strong> Follow the numbered sequence in the diagram for optimal setup. You can copy configurations
                    from existing projects to speed up the process.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Workflow Image */}
          <div className="mb-6">
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
              <img
                src="/images/admin_functions_help.jpg"
                alt="Admin Functions Workflow Diagram"
                className="w-full h-auto rounded-lg shadow-lg"
              />


            </div>
          </div>

          {/* Configuration Steps */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Mandatory Steps */}
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <h4 className="font-semibold text-red-800 mb-3 flex items-center">
                <span className="bg-red-500 text-white px-2 py-1 rounded text-xs font-bold mr-2">MANDATORY</span>
                Required Configuration
              </h4>
              <ul className="space-y-2 text-sm text-red-700">
                <li className="flex items-start">
                  <span className="bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5">1</span>
                  <span><strong>Manage Projects:</strong> Create and configure your project structure</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5">2</span>
                  <span><strong>Manage Functional Hierarchy:</strong> Define requirement mapping levels</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5">5</span>
                  <span><strong>Manage Access Level Definitions:</strong> Configure user permission framework</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5">6</span>
                  <span><strong>Manage Status Codes:</strong> Set up status values for various entities</span>
                </li>
              </ul>
            </div>

            {/* Optional Steps */}
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <h4 className="font-semibold text-amber-800 mb-3 flex items-center">
                <span className="bg-amber-500 text-white px-2 py-1 rounded text-xs font-bold mr-2">OPTIONAL</span>
                Additional Configuration
              </h4>
              <ul className="space-y-2 text-sm text-amber-700">
                <li className="flex items-start">
                  <span className="bg-amber-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5">3</span>
                  <span><strong>Team Definitions:</strong> Configure project team structures</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-amber-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5">4</span>
                  <span><strong>Manage Users:</strong> Set up user accounts and assignments</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-amber-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5">7</span>
                  <span><strong>Exceptions Configuration:</strong> Define requirement exceptions</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-amber-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5">8</span>
                  <span><strong>Provider & Consumer Systems:</strong> Configure integration systems</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-amber-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5">9</span>
                  <span><strong>Products & User Access:</strong> Set up product classifications</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-amber-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5">10</span>
                  <span><strong>Functional Domains:</strong> Define and assign project domains</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Additional Tips */}
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">💡 Pro Tips</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Most configurations can be copied from existing projects to save time</li>
              <li>• Template configurations are automatically loaded during account setup</li>
              <li>• Complete mandatory steps before moving to optional configurations</li>
              <li>• You can return to modify any configuration at any time</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-lg hover:from-rose-600 hover:to-pink-600 transition-all duration-200 font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};





// Work in Progress Dialog Component
const WorkInProgressDialog = ({ isOpen, onClose, featureName = "This feature" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Work in Progress
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors p-2 rounded-full hover:bg-white/20"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          {/* Construction Icon */}
          <div className="mb-4 flex justify-center">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {featureName} is Under Development
          </h3>

          <p className="text-gray-600 mb-4 leading-relaxed">
            We're currently working on this feature to bring you an enhanced experience.
            Please check back soon for updates!
          </p>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4 text-left">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-amber-700">
                  <strong>Expected:</strong> This feature will allow you to copy predefined requirements
                  from templates to accelerate your project setup process.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all duration-200 font-medium"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};





// Modern Gradient Background Component
const GradientBackground = () => (
  <div className="fixed inset-0 -z-10">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"></div>
    <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-200/30 to-transparent rounded-full blur-3xl"></div>
    <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-purple-200/30 to-transparent rounded-full blur-3xl"></div>
  </div>
);

// Modern Project Selection Card Component
const ModernProjectSelection = ({ userId, onProjectSelect, selectedProject, default_project, onSetDefault, showSuccessMessage, onProjectsLoaded }) => (
  <div className="mb-8">
    <GlassCard className="p-8">
      <div className="flex items-center space-x-4 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012 2v2M7 7h10" />
          </svg>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-800">Select Project</h3>
          <p className="text-sm text-gray-600">Choose which project you want to operate on</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-0.5">
            <div className="bg-white rounded-lg h-full flex items-center">
              <ProjectDropdown
                userId={userId}
                onProjectSelect={onProjectSelect}
                onProjectsLoaded={onProjectsLoaded}
              />
            </div>
          </div>
        </div>

        {selectedProject && selectedProject !== default_project && (
          <button
            onClick={onSetDefault}
            className="w-full px-6 py-3 rounded-xl font-medium transition-all duration-300 bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
          >
            <div className="flex items-center justify-center space-x-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Make this my default project</span>
            </div>
          </button>
        )}

        {showSuccessMessage && (
          <div className="w-full px-6 py-3 rounded-xl font-medium bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200 shadow-lg animate-pulse">
            <div className="flex items-center justify-center space-x-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Default project updated successfully</span>
            </div>
          </div>
        )}
      </div>
    </GlassCard>
  </div>
);

// Modern Glass Card Component
const GlassCard = ({ children, className = "" }) => (
  <div className={`bg-white/70 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 ${className}`}>
    {children}
  </div>
);

// Updated Modern Dashboard Card with better active/inactive differentiation
const ModernDashboardCard = ({
  title,
  path,
  onClick,
  iconPath,
  altText,
  iconSize = "w-10 h-10", // Default size
  gradientFrom = "from-blue-500",
  gradientTo = "to-blue-600",
  disabled = false
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Helper function to get text color from gradient
  const getTextColorFromGradient = (from, to) => {
    // Check for blue/cyan combinations
    if (from.includes('blue') || to.includes('blue') || from.includes('cyan') || to.includes('cyan')) {
      console.log('Gradient colors matched: blue/cyan');
      return 'text-blue-700 group-hover:text-blue-800';
    }
      // Check for rose combinations (Admin Functions section)
    if (from.includes('rose') || to.includes('rose')) {
      console.log('Gradient colors matched: rose');
      return 'text-pink-700 group-hover:text-pink-800';
    }
      // Check for purple/pink combinations (Project Management section)
    if (from.includes('purple') || to.includes('purple') || from.includes('pink') || to.includes('pink')) {
      console.log('Gradient colors matched: purple/pink');
      return 'text-indigo-700 group-hover:text-indigo-800';
    }
    // Check for green/emerald combinations
    if (from.includes('green') || to.includes('green') || from.includes('emerald') || to.includes('emerald')) {
      console.log('Gradient colors matched: green/emerald');
      return 'text-green-700 group-hover:text-green-800';
    }

    // Check for orange/red combinations (Super Admin section)
    if (from.includes('orange') || to.includes('orange') || from.includes('red') || to.includes('red')) {
      console.log('Gradient colors matched: orange/red');
      return 'text-orange-700 group-hover:text-orange-800';
    }

    // Debug logging to see what colors are being passed
    console.log('Gradient colors not matched:', from, to);

    // default fallback
    return 'text-gray-700 group-hover:text-gray-800';
  };

  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    if (onClick) {
      onClick();
    }
  };

  const cardContent = (
    <div
      className="group relative overflow-hidden"
      onMouseEnter={() => disabled && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <GlassCard className={`p-6 h-full min-h-[140px] flex flex-col items-center justify-center text-center transition-all duration-300 ${
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:shadow-2xl hover:scale-[1.02] cursor-pointer'
      }`}>
        {/* Gradient background on hover - only for active cards */}
        <div className={`absolute inset-0 bg-gradient-to-br ${gradientFrom} ${gradientTo} ${
          disabled
            ? 'opacity-0'
            : 'opacity-0 group-hover:opacity-5'
        } transition-opacity duration-300 rounded-2xl`}></div>

        {/* Icon container with very thin border using section color theme */}
        <div className={`relative mb-4 p-0.5 rounded-2xl transition-all duration-300 ${
          disabled
            ? 'bg-gray-200'
            : `bg-gradient-to-br ${gradientFrom} ${gradientTo}`
        }`}>
          <div className={`rounded-xl p-2 flex items-center justify-center transition-all duration-300 ${
            disabled
              ? 'bg-gray-50'
              : 'bg-white'
          }`}>
            <img
              src={iconPath}
              alt={altText}
              className={`${iconSize} object-contain transition-all duration-300 ${
                disabled
                  ? 'grayscale opacity-50'
                  : 'group-hover:scale-110'
              }`}
            />
          </div>
        </div>

        <h3 className={`text-sm font-semibold transition-colors duration-300 ${
          disabled
            ? 'text-gray-400'
            : getTextColorFromGradient(gradientFrom, gradientTo)
        }`}>
          {title}
        </h3>

        {/* Disabled overlay */}
        {disabled && (
          <div className="absolute inset-0 bg-gray-100/50 backdrop-blur-[1px] rounded-2xl flex items-center justify-center">
            <div className="bg-gray-600 text-white px-2 py-1 rounded text-xs font-medium">
              Disabled
            </div>
          </div>
        )}

        {/* Hover indicator - only for active cards */}
        <div className={`absolute bottom-2 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-opacity duration-300 ${
          disabled
            ? 'opacity-0'
            : 'opacity-0 group-hover:opacity-100'
        }`}></div>

        {/* Tooltip for disabled state */}
        {disabled && showTooltip && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 bg-gray-800 text-white px-3 py-2 rounded-lg text-xs whitespace-nowrap shadow-lg">
            Please select a project to activate these options
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full border-4 border-transparent border-t-gray-800"></div>
          </div>
        )}
      </GlassCard>
    </div>
  );

  if (disabled) {
    return (
      <div onClick={handleClick}>
        {cardContent}
      </div>
    );
  }

  return path ? (
    <Link to={path} className="block">
      {cardContent}
    </Link>
  ) : (
    <div onClick={onClick}>
      {cardContent}
    </div>
  );
};

// Modern Section Header with gradient and matching text colors
// Modern Section Header with gradient, matching text colors, and optional help button
const ModernSectionHeader = ({ title, gradient = "from-blue-500 to-purple-600", onHelpClick = null }) => {
  // Map gradient colors to their darker background equivalents and text colors
  const getBackgroundAndTextColor = (gradient) => {
    if (gradient.includes("blue") && gradient.includes("cyan")) {
      return {
        background: "bg-gradient-to-r from-blue-100 to-cyan-200",
        textColor: "text-blue-800"
      };
    }
    if (gradient.includes("purple") && gradient.includes("pink")) {
      return {
        background: "bg-gradient-to-r from-purple-100 to-pink-200",
        textColor: "text-purple-800"
      };
    }
    if (gradient.includes("green") && gradient.includes("emerald")) {
      return {
        background: "bg-gradient-to-r from-green-100 to-emerald-200",
        textColor: "text-green-800"
      };
    }
    if (gradient.includes("rose") && gradient.includes("pink")) {
      return {
        background: "bg-gradient-to-r from-rose-100 to-pink-200",
        textColor: "text-rose-800"
      };
    }
    if (gradient.includes("orange") && gradient.includes("red")) {
      return {
        background: "bg-gradient-to-r from-orange-100 to-red-200",
        textColor: "text-orange-800"
      };
    }
    // default
    return {
      background: "bg-gradient-to-r from-blue-100 to-purple-200",
      textColor: "text-blue-800"
    };
  };

  const { background, textColor } = getBackgroundAndTextColor(gradient);

  return (
    <div className="mb-8">
      <div className={`${background} rounded-2xl p-6 shadow-lg`}>
    <div className="flex items-center justify-center relative">
          <h2 className={`text-xl font-bold ${textColor}`}>
            {title}
          </h2>


        {onHelpClick && (
          <button
            onClick={onHelpClick}
            className={`absolute right-0 ${textColor} hover:opacity-70 transition-opacity p-2 rounded-lg hover:bg-white/30`}
            title="Help"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        )}



        </div>
      </div>
    </div>
  );
};

const RequirementsPortal = () => {



  const user_id = localStorage.getItem('user_id');
  const corporate_account = localStorage.getItem('corporate_account');
  const account_description = localStorage.getItem('account_description');
  const login_token = localStorage.getItem('authToken');
  const default_project = localStorage.getItem('default_project');
  const current_project = localStorage.getItem('current_project');




  const [isSuccess, setIsSuccess] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);
  
  // Add state for Legacy Code Analysis Dialog
  const [isLegacyAnalysisDialogOpen, setIsLegacyAnalysisDialogOpen] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableCriticalities, setAvailableCriticalities] = useState([]);
  const [availablePriorities, setAvailablePriorities] = useState([]);


const [isAdminHelpDialogOpen, setIsAdminHelpDialogOpen] = useState(false);

const [isWipDialogOpen, setIsWipDialogOpen] = useState(false);

const handleCopyRequirementsClick = () => {
  setIsWipDialogOpen(true);
};

  const handleSetDefaultProject = async () => {
    const result = await changeUserDefaultProject({
      corporate_account,
      user_id,
      project_id: selectedProject,
      login_token
    });
    if (result.success) {
      localStorage.setItem('default_project', selectedProject);
      setIsSuccess(true);
      setShowSuccessMessage(true);

      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    } else {
      alert(`Failed to set default project: ${result.message}`);
    }
  };


// Function to handle Admin Help button click
const handleAdminHelpClick = () => {
  setIsAdminHelpDialogOpen(true);
};

  // New function to handle projects loaded from dropdown
  const handleProjectsLoaded = (projects) => {
    console.log(`Projects loaded: ${projects.length} projects`);
    setAvailableProjects(projects);

    // If there's only one project and no default project is set, automatically set it as default
    if (projects.length === 1 && (!default_project || default_project === 'null')) {
      const singleProject = projects[0];
      const projectId = singleProject.id || singleProject.project_id || singleProject.value;

      if (projectId) {
        // Set as selected project
        setSelectedProject(projectId);

        // Automatically set as default project
        const autoSetDefault = async () => {
          try {
            const result = await changeUserDefaultProject({
              corporate_account,
              user_id,
              project_id: projectId,
              login_token
            });

            if (result.success) {
              localStorage.setItem('default_project', projectId);
              setIsSuccess(true);
              setShowSuccessMessage(true);

              // Hide success message after 3 seconds
              setTimeout(() => {
                setShowSuccessMessage(false);
              }, 3000);
            } else {
              console.error('Failed to auto-set default project:', result.message);
            }
          } catch (error) {
            console.error('Error auto-setting default project:', error);
          }
        };

        autoSetDefault();
      }
    }
  };

  // Function to fetch requirement configuration data
  const fetchRequirementConfig = async (projectId) => {
    if (!projectId || projectId === 'null') return;

    try {
      // Fetch statuses
      const statusResponse = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporate_account,
          project_id: projectId,
          entity: 'REQUIREMENT',
          search_query: ''
        })
      });

      const statusData = await statusResponse.json();
      if (statusData.status === 'Success') {
        setAvailableStatuses(statusData.entity_statuses.map(s => s.status));
      }

      // Fetch criticalities
      const criticalityResponse = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporate_account,
          project_id: projectId,
          entity: 'REQUIREMENT_CRITICALITY',
          search_query: ''
        })
      });

      const criticalityData = await criticalityResponse.json();
      if (criticalityData.status === 'Success') {
        setAvailableCriticalities(criticalityData.entity_statuses.map(c => c.status));
      }

      // Fetch priorities
      const priorityResponse = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporate_account,
          project_id: projectId,
          entity: 'REQUIREMENT_PRIORITY',
          search_query: ''
        })
      });

      const priorityData = await priorityResponse.json();
      if (priorityData.status === 'Success') {
        setAvailablePriorities(priorityData.entity_statuses.map(p => p.status));
      }

    } catch (error) {
      console.error('Error fetching requirement configuration:', error);
    }
  };

  const [selectedProject, setSelectedProject] = useState(null);
  const handleProjectSelect = (projectId) => {
    // Reset success states when changing projects
    setIsSuccess(false);
    setShowSuccessMessage(false);

    // Set the new selected project
    setSelectedProject(projectId);
  };

  const [access_level, setAccessLevel] = useState(1);

  useEffect(() => {
    const setInitialLoad = async () => {
      setAccessLevel(1);
    };

    const initialProject = current_project || default_project || null;
    setSelectedProject(initialProject);

    setInitialLoad();
  }, []); // Run only on mount

  useEffect(() => {
    const fetchData = async () => {
      if (selectedProject) {
        try {
          // Fetch user access level
          console.log(`Selected Project is 5555 : ${selectedProject}`)
          console.log(`Default Project is 5555: ${default_project}`)
          console.log(`Current Project is 5555: ${current_project}`)

          const userAccessResponse = await fetch(appConfig.getUrl('/get_user_info'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${login_token}`
            },
            body: JSON.stringify({
              user_id,
              project_id: selectedProject
            })
          });

          const userAccessData = await userAccessResponse.json();
          if (userAccessData.status === 'Success') {
            setAccessLevel(userAccessData.access_level);

            console.log(`Selected Project is 55 : ${selectedProject}`)
            console.log(`Default Project is 55: ${default_project}`)

            localStorage.setItem('current_project', selectedProject)
            console.log(`Current Project is 55: ${current_project}`)

            // Fetch requirement configuration for legacy analysis
            await fetchRequirementConfig(selectedProject);

            if (default_project == 'null' && selectedProject != 'null') {
              console.log(`Criteria met`);

              // Directly make the API call
              try {
                const result = await changeUserDefaultProject({
                  corporate_account,
                  user_id,
                  project_id: selectedProject,
                  login_token
                });

                if (result.success) {
                  console.log(`Criteria met 2`);
                  localStorage.setItem('default_project', selectedProject);
                  setIsSuccess(true);
                  setShowSuccessMessage(true);

                  // Hide success message after 3 seconds
                  setTimeout(() => {
                    setShowSuccessMessage(false);
                  }, 3000);
                } else {
                  console.error('Failed to set default project:', result.message);
                }
              } catch (error) {
                console.error('Error calling API:', error);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      }
    };

    fetchData();
  }, [selectedProject, corporate_account, user_id, login_token]);

  // Function to handle Legacy Code Analysis button click
  const handleLegacyAnalysisClick = () => {
    if (!selectedProject || selectedProject === 'null') {
      alert('Please select a project before using Legacy Code Analysis');
      return;
    }
    setIsLegacyAnalysisDialogOpen(true);
  };

  // Check if project is selected for disabling links
  const isProjectSelected = selectedProject && selectedProject !== 'null';




  return (
    <div className="min-h-screen">
      <GradientBackground />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <Header />

        <div className="max-w-7xl mx-auto mt-10 px-2">
          {/* Modern Project Selection */}
          <ModernProjectSelection
            userId={user_id}
            onProjectSelect={handleProjectSelect}
            selectedProject={selectedProject}
            default_project={default_project}
            onSetDefault={handleSetDefaultProject}
            showSuccessMessage={showSuccessMessage}
            onProjectsLoaded={handleProjectsLoaded}
          />

          {/* My Dashboard & Project Management Sections - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-12 mt-8">
            {/* My Dashboard Section - Takes 3/5 width */}
            <div className="lg:col-span-3">
              <ModernSectionHeader
                title="My Dashboard"
                gradient="from-blue-500 to-cyan-500"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ModernDashboardCard
                  title="My Requirement Approvals"
                  path="/MyRequirementApprovals"
                  iconPath="/icons/noun-approval-6293848-12B0FB.svg"
                  altText="Requirement Approvals Icon"
                  iconSize="w-10 h-10"
                  gradientFrom="from-blue-500"
                  gradientTo="to-cyan-500"
                  disabled={!isProjectSelected}
                />
                <ModernDashboardCard
                  title="My RAID Assignments"
                  path="/RaidLogManagement?mode=myassignments"
                  iconPath="/icons/noun-todo-6749786-12B0FB.svg"
                  altText="RAID Assignments Icon"
                  iconSize="w-10 h-10"
                  gradientFrom="from-blue-500"
                  gradientTo="to-cyan-500"
                  disabled={!isProjectSelected}
                />
              </div>
            </div>

            {/* Project Management Section - Takes 2/5 width */}
            <div className="lg:col-span-2">
              <ModernSectionHeader
                title="Project Management"
                gradient="from-purple-500 to-pink-500"
              />
              <div className="grid grid-cols-1 gap-6">
                <ModernDashboardCard
                  title="Manage RAID Log"
                  path="/RaidLogManagement?mode=allassignees"
                  iconPath="/icons/RAID.svg"
                  altText="RAID Log Icon"
                  iconSize="w-10 h-10"
                  gradientFrom="from-purple-500"
                  gradientTo="to-pink-500"
                    textColor="text-red-600 hover:text-purple-700"
                  disabled={!isProjectSelected}
                />
              </div>
            </div>
          </div>










          {/* Requirements Management Section */}
          <div className="mb-16">
            <ModernSectionHeader
              title="Requirements Management"
              gradient="from-green-500 to-emerald-500"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <ModernDashboardCard
                title="Functional Requirements"
                path="/BaseRequirementsManagement"
                iconPath="/icons/noun-technical-document-5340526-007435.svg"
                altText="Functional Requirements Icon"
                iconSize="w-10 h-10"
                gradientFrom="from-green-500"
                gradientTo="to-emerald-500"
                disabled={!isProjectSelected}
              />
              <ModernDashboardCard
                title="Integration Requirements"
                path="/IntegrationRequirementsManagement"
                iconPath="/icons/noun-network-7456881-007435.svg"
                altText="Integration Requirements Icon"
                iconSize="w-10 h-10"
                gradientFrom="from-green-500"
                gradientTo="to-emerald-500"
                disabled={!isProjectSelected}
              />
              {/* REPLACED: Integration Fields with Legacy Code Analysis */}
              <ModernDashboardCard
                title="Generate Requirements from Code Using AI"
                onClick={handleLegacyAnalysisClick}
                iconPath="/logos/AI-Logo.png"
                altText="Legacy Code Analysis Icon"
                iconSize="w-10 h-10"
                gradientFrom="from-green-500"
                gradientTo="to-emerald-500"
                disabled={!isProjectSelected}
              />








              {/* Admin Help Dialog */}
                <AdminHelpDialog
                  isOpen={isAdminHelpDialogOpen}
                  onClose={() => setIsAdminHelpDialogOpen(false)}
                />


            </div>
          </div>








                   {/* Reports Section */}
            <div className="mb-16">
              <ModernSectionHeader
                title="Reports"
                gradient="from-indigo-500 to-purple-500"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <ModernDashboardCard
                  title="Requirements Counts By Status and Priority"
                  path="/StatusPriorityReport"
                  iconPath="/icons/noun-report-7976410-C462DD.svg"
                  altText="Requirements Counts By Status and Priority Icon"
                  iconSize="w-10 h-10"
                  gradientFrom="from-indigo-500"
                  gradientTo="to-purple-500"
                  disabled={!isProjectSelected}
                />



                <ModernDashboardCard
                  title="Requirements Approval Status Report"
                  path="/RequirementApprovalDashboard"
                  iconPath="/icons/noun-approve-7871545-C462DD.svg"
                  altText="Requirements Approval Status Report"
                  iconSize="w-10 h-10"
                  gradientFrom="from-indigo-500"
                  gradientTo="to-purple-500"
                  disabled={!isProjectSelected}
                />

                <ModernDashboardCard
                  title="Product Evaluation Report"
                  path="/ProductClassificationReport"
                  iconPath="/icons/noun-decision-tree-6639444-C462DD.svg"
                  altText="Product Evaluation Report"
                  iconSize="w-10 h-10"
                  gradientFrom="from-indigo-500"
                  gradientTo="to-purple-500"
                  disabled={!isProjectSelected}
                />

            </div>
          </div>






          {/* Admin Functions Section */}
          <div className="mb-16">



<ModernSectionHeader
  title="Admin Functions"
  gradient="from-rose-500 to-pink-500"
  onHelpClick={handleAdminHelpClick}
/>


            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">

              <ModernDashboardCard
                title="Manage Projects"
                path="/ManageProjects"
                iconPath="/icons/noun-project-7663345-FF59B9.svg"
                altText="Manage Projects Icon"
                iconSize="w-10 h-10"
                gradientFrom="from-rose-500"
                gradientTo="to-pink-500"
              />

              <ModernDashboardCard
                title="Manage Functional hierarchy"
                path="/FunctionalLevels"
                iconPath="/icons/noun-hierarchy-6761980-FF59B9.svg"
                altText="Functional Hierarchy Icon"
                iconSize="w-10 h-10"
                gradientFrom="from-rose-500"
                gradientTo="to-pink-500"
                disabled={!isProjectSelected}
              />

              <ModernDashboardCard
                title="Manage Project Team Definitions"
                path="/BusinessTeams"
                iconPath="/icons/noun-project-team-6098168-FF59B9.svg"
                altText="Team Management Icon"
                iconSize="w-10 h-10"
                gradientFrom="from-rose-500"
                gradientTo="to-pink-500"
                disabled={!isProjectSelected}
              />
              <ModernDashboardCard
                title="Manage Users"
                path="/ManageUsers"
                iconPath="/icons/noun-users-gear-6133670-FF59B9.svg"
                altText="Manage Users Icon"
                iconSize="w-10 h-10"
                gradientFrom="from-rose-500"
                gradientTo="to-pink-500"
              />



              <ModernDashboardCard
                title="Manage Access Level Definitions"
                path="/ManageAccessLevels"
                iconPath="/icons/noun-access-7571533-FF59B9.svg"
                altText="Manage Access Level Definitions Icon"
                iconSize="w-10 h-10"
                gradientFrom="from-rose-500"
                gradientTo="to-pink-500"
              />

              <ModernDashboardCard
                title="Manage Status Codes"
                path="/ManageStatusCodes"
                iconPath="/icons/noun-configuration-7282760-FF59B9.svg"
                altText="Manage Status Values Icon"
                iconSize="w-10 h-10"
                gradientFrom="from-rose-500"
                gradientTo="to-pink-500"
                disabled={!isProjectSelected}
              />


              <ModernDashboardCard
                title="Manage Exceptions Configuration"
                path="/FunctionalAttributes"
                iconPath="/icons/noun-exception-7162434-FF59B9.svg"
                altText="Exceptions Configuration Icon"
                iconSize="w-10 h-10"
                gradientFrom="from-rose-500"
                gradientTo="to-pink-500"
                disabled={!isProjectSelected}
              />
              <ModernDashboardCard
                title="Manage Provider & Consumer Systems"
                path="/IntegrationSystems"
                iconPath="/icons/noun-system-6349587-FF59B9.svg"
                altText="Application Systems Icon"
                iconSize="w-10 h-10"
                gradientFrom="from-rose-500"
                gradientTo="to-pink-500"
                disabled={!isProjectSelected}
              />
              <ModernDashboardCard
                title="Manage Products & User Access To Products"
                path="/ManageProducts"
                iconPath="/icons/noun-application-6585310-FF59B9.svg"
                altText="Products Access Icon"
                iconSize="w-10 h-10"
                gradientFrom="from-rose-500"
                gradientTo="to-pink-500"
                disabled={!isProjectSelected}
              />

              <ModernDashboardCard
                title="Manage Functional Domains"
                path="/FunctionalDomains"
                iconPath="/icons/noun-feature-6071822-FF59B9.svg"
                altText="Manage Functional Domains Icon"
                iconSize="w-10 h-10"
                gradientFrom="from-rose-500"
                gradientTo="to-pink-500"
              />


                <ModernDashboardCard
                  title="Copy Preloaded Requirements"
                  onClick={handleCopyRequirementsClick}  // Changed from path to onClick
                  iconPath="/icons/noun-copy-6432717-FF59B9.svg"
                  altText="Copy Requirements Icon"
                  iconSize="w-10 h-10"
                  gradientFrom="from-rose-500"
                  gradientTo="to-pink-500"
                  disabled={!isProjectSelected}
                />




            </div>
          </div>



        </div>

        {/* Legacy Code Analysis Dialog */}
        <EnhancedLegacyCodeAnalysisDialog
          isOpen={isLegacyAnalysisDialogOpen}
          onClose={() => setIsLegacyAnalysisDialogOpen(false)}
          functionalLevelPath="Please select a functional level within the dialog"
          corporateAccount={corporate_account}
          currentProject={selectedProject}
          login_token={login_token}
          availableStatuses={availableStatuses}
          availableCriticalities={availableCriticalities}
          availablePriorities={availablePriorities}
          userId={user_id}
          userEmail={user_id}
        />

        {/* Work in Progress Dialog */}
        <WorkInProgressDialog
          isOpen={isWipDialogOpen}
          onClose={() => setIsWipDialogOpen(false)}
          featureName="Copy Preloaded Requirements"
        />

      </div>
    </div>
  );
};

export default RequirementsPortal;